// javascript
class RoomixDeliveryCalendar extends HTMLElement {
  constructor() {
    super();
    this.summarySelector = this.getAttribute('summary-selector')
      || 'roomix-summary .summary-step.delivery-summary span[data-type="delivery-date"], span[data-type="delivery-date"]';
    this.locale = this.getAttribute('locale') || 'en-GB';
    this._calendar = null;
  }

  connectedCallback() {
    // render minimal DOM (light DOM to avoid library shadow issues)
    this.innerHTML = `
      <div class="field">
        <input class="field__input" type="text" id="selected-date" name="properties[Delivery Date]">
        <label class="field__label" for="selected-date">Delivery Date</label>
      </div>
    `;

    this.inputEl = this.querySelector('#selected-date');
    if (!this.inputEl) return;

    this._ensureResources().then(() => this._initCalendar()).catch(() => {
      // if resources can't load, leave input alone
    });
  }

  // load vanilla-calendar-pro JS/CSS if not present
  _ensureResources() {
    if (window.VanillaCalendarPro && window.VanillaCalendarPro.Calendar) return Promise.resolve();
    const cssUrls = [
      'https://cdn.jsdelivr.net/npm/vanilla-calendar-pro/styles/index.css',
      'https://cdn.jsdelivr.net/npm/vanilla-calendar-pro/styles/themes/light.css'
    ];
    const jsUrl = 'https://cdn.jsdelivr.net/npm/vanilla-calendar-pro/index.js';

    const loadCss = (href) => new Promise((res) => {
      if (document.querySelector(`link[href="${href}"]`)) return res();
      const l = document.createElement('link');
      l.rel = 'stylesheet';
      l.href = href;
      l.onload = () => res();
      l.onerror = () => res();
      document.head.appendChild(l);
    });

    const loadJs = (src) => new Promise((res, rej) => {
      if (window.VanillaCalendarPro && window.VanillaCalendarPro.Calendar) return res();
      if (document.querySelector(`script[src="${src}"]`)) {
        // wait a tick for library readiness
        const wait = () => (window.VanillaCalendarPro && window.VanillaCalendarPro.Calendar) ? res() : setTimeout(wait, 50);
        return wait();
      }
      const s = document.createElement('script');
      s.src = src;
      s.defer = true;
      s.onload = () => {
        // library should attach to window
        if (window.VanillaCalendarPro && window.VanillaCalendarPro.Calendar) return res();
        // small wait loop
        const wait = () => (window.VanillaCalendarPro && window.VanillaCalendarPro.Calendar) ? res() : setTimeout(wait, 50);
        wait();
      };
      s.onerror = () => rej(new Error('failed to load calendar script'));
      document.head.appendChild(s);
    });

    return Promise.all(cssUrls.map(loadCss)).then(() => loadJs(jsUrl));
  }

  // --- utility functions (kept minimal and same logic as original) ---
  _pad(n){ return n < 10 ? '0' + n : String(n); }
  _formatDate(d){ return d.getFullYear() + '-' + this._pad(d.getMonth() + 1) + '-' + this._pad(d.getDate()); }
  _addDays(d, days){ const r = new Date(d); r.setDate(r.getDate() + days); return r; }
  _moveOffWeekend(d){
    const day = d.getDay();
    if (day === 6) return this._addDays(d, 2);
    if (day === 0) return this._addDays(d, 1);
    return d;
  }
  _addBusinessDays(date, days) {
    const r = new Date(date);
    while (days > 0) {
      r.setDate(r.getDate() + 1);
      const dow = r.getDay();
      if (dow !== 0 && dow !== 6) days--;
    }
    return r;
  }
  _computeDateRange(){
    const now = new Date();
    const cutoffHour = 17;
    const start = (now.getHours() >= cutoffHour)
      ? this._moveOffWeekend(this._addDays(now, 1))
      : now;
    const dateMinCandidate = this._addBusinessDays(start, 2);
    const dateMax = this._addDays(now, 18);
    return {
      dateMin: this._formatDate(dateMinCandidate),
      dateMax: this._formatDate(dateMax)
    };
  }
  _parseIsoOrDate(val) {
    if (!val) return null;
    if (val instanceof Date) return val;
    if (typeof val === 'string') {
      const m = val.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      const d = new Date(val);
      return isNaN(d.getTime()) ? null : d;
    }
    return null;
  }
  _formatDeliveryDate(val) {
    const d = this._parseIsoOrDate(val);
    if (!d) return '';
    const weekday = d.toLocaleDateString(this.locale, { weekday: 'short' }); // "Mon"
    const day = d.getDate();
    const month = d.toLocaleDateString(this.locale, { month: 'short' });     // "Dec"
    const year = d.getFullYear();
    return `${weekday} ${day} ${month} ${year}`;
  }
  _updateDeliverySummary(valueOrDate) {
    const text = this._formatDeliveryDate(valueOrDate);
    const el = document.querySelector(this.summarySelector);
    if (el) el.textContent = text;
  }
  _extractMonth(isoDateStr) {
    const [year, month] = String(isoDateStr).split('-');
    return Number(month) - 1;
  }
  _extractYear(isoDateStr) {
    const [year] = String(isoDateStr).split('-');
    return Number(year);
  }

  _initCalendar() {
    const { Calendar } = window.VanillaCalendarPro || {};
    if (!Calendar) return;

    const range = this._computeDateRange();
    this.inputEl.value = range.dateMin;

    const options = {
      inputMode: true,
      locate: this.locale,
      positionToInput: 'auto',
      dateMin: range.dateMin,
      dateMax: range.dateMax,
      selectedTheme: 'light',
      disableDates: ['2025-12-25', '2025-12-26', '2025-12-31', '2026-01-01'], // example fixed holidays
      disableWeekdays: [0, 6],
      selectedDates: [range.dateMin],
      selectedMonth: this._extractMonth(range.dateMin),
      selectedYear: this._extractYear(range.dateMin),
      onInit: (self) => {
        const selected = self && self.context && self.context.selectedDates && self.context.selectedDates[0];
        const isoVal = selected instanceof Date ? this._formatDate(selected) : String(selected || this.inputEl.value || '');
        this.inputEl.value = isoVal;
        this._updateDeliverySummary(isoVal);
      },
      onChangeToInput: (self) => {
        const input = self.context && (self.context.inputElement || this.inputEl);
        const selected = self.context && self.context.selectedDates && self.context.selectedDates[0];
        if (!input) return;
        if (selected) {
          const value = selected instanceof Date ? selected.toISOString().split('T')[0] : String(selected);
          input.value = value;
          this._updateDeliverySummary(value);
          if (typeof self.hide === 'function') {
            try { self.hide(); } catch (e) { /* ignore */ }
          }
        } else {
          input.value = '';
          this._updateDeliverySummary(null);
        }
      }
    };

    // instantiate and init
    this._calendar = new Calendar(this.inputEl, options);
    this._calendar.init();

    // ensure summary updated right after init (defensive)
    try {
      const selected = (this._calendar && this._calendar.context && this._calendar.context.selectedDates && this._calendar.context.selectedDates[0]) || this.inputEl.value || null;
      const iso = selected instanceof Date ? this._formatDate(selected) : (typeof selected === 'string' ? selected : (this.inputEl && this.inputEl.value) || '');
      if (iso) this._updateDeliverySummary(iso);
    } catch (e) { /* ignore */ }
  }

  disconnectedCallback() {
    // if library exposes destroy/hide, try to cleanup
    try {
      if (this._calendar && typeof this._calendar.hide === 'function') this._calendar.hide();
    } catch (e) {}
  }
}

customElements.define('delivery-calendar', RoomixDeliveryCalendar);
